import {
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { base_url } from 'env'
import { useState, useEffect } from 'react'
import { useLoaderData, useNavigate } from 'react-router'
import { ActivityModel } from 'types'
import axios from 'axios'
import { toast } from 'react-hot-toast'

export default function ActivityEditPage() {
  const navigate = useNavigate()
  const initialData = useLoaderData() as ActivityModel
  const [activity, setActivity] = useState(initialData)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setActivity(initialData)
  }, [initialData])

  const handleSubmit = () => {
    if (!activity.title || !activity.instructions) {
      toast.error('Le titre et les instructions sont obligatoires.')
      return
    }
    setIsSaving(true)
    const token = localStorage.getItem('token')
    if (token == null) {
      navigate('/login')
      return
    }

    const payload = {
      ...activity,
      instructionsCloseCircle: activity.instructionsCloseCircle || null,
      instructionsOther: activity.instructionsOther || null,
    }

    axios
      .put(base_url + `/activity/${activity.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        toast.success('Activité mise à jour avec succès !')
        navigate(`/activity/${activity.id}`)
      })
      .catch((error) => {
        console.error(
          'Error updating activity:',
          error.response?.data || error.message
        )
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <PageAppBar pageName="Modifier une Activité">
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          alignItems: 'center',
          padding: '2rem',
          maxWidth: '700px',
          margin: 'auto',
        }}
        noValidate
        autoComplete="off"
      >
        <Typography variant="h5" component="h2">
          Modifier: {activity.title}
        </Typography>
        <TextField
          required
          fullWidth
          id="title"
          label="Titre"
          value={activity.title}
          variant="outlined"
          onChange={(e) => setActivity({ ...activity, title: e.target.value })}
          disabled={isSaving}
        />
        <TextField
          required
          fullWidth
          multiline
          rows={4}
          id="instructions"
          label="Instructions (Parents)"
          value={activity.instructions}
          variant="outlined"
          onChange={(e) =>
            setActivity({ ...activity, instructions: e.target.value })
          }
          disabled={isSaving}
        />
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Instructions pour le cercle proche (Optionnel)"
          value={activity.instructionsCloseCircle || ''}
          variant="outlined"
          onChange={(e) =>
            setActivity({
              ...activity,
              instructionsCloseCircle: e.target.value,
            })
          }
          disabled={isSaving}
        />
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Instructions pour une autre personne (Optionnel)"
          value={activity.instructionsOther || ''}
          variant="outlined"
          onChange={(e) =>
            setActivity({ ...activity, instructionsOther: e.target.value })
          }
          disabled={isSaving}
        />
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="support-label">Support de lecture</InputLabel>
          <Select
            labelId="support-label"
            value={activity.support}
            label="Support de lecture"
            onChange={(e) =>
              setActivity({
                ...activity,
                support: e.target.value as ActivityModel['support'],
              })
            }
          >
            <MenuItem value="none">Aucun</MenuItem>
            <MenuItem value="comic_book">Bande dessinée</MenuItem>
            <MenuItem value="picture_book">Album illustré</MenuItem>
            <MenuItem value="youth_novel">Roman jeunesse</MenuItem>
            <MenuItem value="youth_magazine">Magazine jeunesse</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="category-label">Catégorie</InputLabel>
          <Select
            labelId="category-label"
            id="category"
            value={activity.category}
            label="Catégorie"
            onChange={(e) =>
              setActivity({
                ...activity,
                category: e.target.value as ActivityModel['category'],
              })
            }
          >
            <MenuItem value="ludique">Ludique</MenuItem>
            <MenuItem value="auto">Autonomie</MenuItem>
            <MenuItem value="renf">Renforcement</MenuItem>
            <MenuItem value="partag">Partage</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="difficulty-label">Difficulté de base</InputLabel>
          <Select
            labelId="difficulty-label"
            id="base_difficulty"
            value={activity.baseDifficulty}
            label="Difficulté de base"
            onChange={(e) =>
              setActivity({
                ...activity,
                baseDifficulty: Number(
                  e.target.value
                ) as ActivityModel['baseDifficulty'],
              })
            }
          >
            {[1, 2, 3, 4, 5].map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={isSaving}>
          <InputLabel id="pleasure-label">Plaisir de base</InputLabel>
          <Select
            labelId="pleasure-label"
            value={activity.basePleasure}
            label="Plaisir de base"
            onChange={(e) =>
              setActivity({
                ...activity,
                basePleasure: Number(
                  e.target.value
                ) as ActivityModel['basePleasure'],
              })
            }
          >
            {[1, 2, 3, 4, 5].map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ mt: 2, minWidth: '150px' }}
        >
          {isSaving ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Sauvegarder les modifications'
          )}
        </Button>
      </Box>
    </PageAppBar>
  )
}
